import React, { useState, useMemo, useEffect } from 'react';
import { HiPlus, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import './MultiLanguageTextInput.scss';

interface LanguageValues {
  en: string;
  ar: string;
}

interface MultiLanguageTextInputProps {
  value: string; // JSON string or plain string
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  isRTL?: boolean;
  multiline?: boolean;
  rows?: number;
}

export const MultiLanguageTextInput: React.FC<MultiLanguageTextInputProps> = ({
  value,
  onChange,
  label,
  placeholder = '',
  error,
  disabled = false,
  required = false,
  isRTL = false,
  multiline = false,
  rows = 3,
}) => {
  // Parse the value to get language-specific values
  const languageValues = useMemo<LanguageValues>(() => {
    try {
      // Try to parse as JSON
      if (value && value.startsWith('{')) {
        const parsed = JSON.parse(value);
        return {
          en: parsed.en || '',
          ar: parsed.ar || '',
        };
      }
    } catch (e) {
      // Not valid JSON, treat as plain string
    }
    // Fallback: treat as English
    return {
      en: value || '',
      ar: '',
    };
  }, [value]);

  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-expand if Arabic translation exists
  useEffect(() => {
    setIsExpanded(!!languageValues.ar);
  }, [languageValues.ar]);

  const handleLanguageChange = (lang: 'en' | 'ar', newValue: string) => {
    const updated = { ...languageValues, [lang]: newValue };
    onChange(JSON.stringify(updated));
  };

  const renderInput = (lang: 'en' | 'ar', val: string, ph: string, dir: 'ltr' | 'rtl') => {
    const commonProps = {
      className: `form-control ${error ? 'is-invalid' : ''} ${lang === 'ar' ? 'language-input' : ''}`,
      value: val,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleLanguageChange(lang, e.target.value),
      placeholder: ph,
      disabled: disabled,
      dir: dir,
    };

    if (multiline) {
      return <textarea {...commonProps} rows={rows} />;
    }
    return <input type="text" {...commonProps} />;
  };

  return (
    <div className={`multi-language-input ${error ? 'has-error' : ''}`}>
      <div className="input-header">
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
        <button
          type="button"
          className="language-toggle-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
          title={isExpanded ? 'Hide languages' : 'Add translations'}
        >
          {isExpanded ? <HiChevronUp /> : <HiPlus />}
        </button>
      </div>

      {/* English field - always visible */}
      <div className="language-group english-group">
        {renderInput('en', languageValues.en, placeholder, 'ltr')}
      </div>

      {/* Arabic field - shown when expanded */}
      {isExpanded && (
        <div className="language-group arabic-group">
          <label className="language-label">العربية</label>
          {renderInput('ar', languageValues.ar, 'الترجمة العربية', 'rtl')}
        </div>
      )}

      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

